import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Space, Collapse, Typography } from 'antd';
import {
  PlusOutlined,
  ImportOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  IndexFund,
  selectors,
  updateIndexFund,
  deleteIndexFund,
  requestIndexFundPreview,
  clearIndexFundPreview,
} from 'state';
import EditableTable from '../../EditableTable';
import IndexFundPreviewModal from '../IndexFundPreviewModal';
import FundsTable from './FundsTable';
import FundDrawer from './FundDrawer';
import DataOpsModal from './DataOpsModal';
import { SessionLite } from './UsageCell';

const { Text } = Typography;

/**
 * v2 Index Funds admin surface: a read-optimized table (live EoD toggle, usage popover) + a
 * create/edit drawer + a cleaned data-ops modal, with search/filter. The decommissioned funds keep
 * their classic collapsible EditableTable, unchanged, per requirement.
 */
const IndexFundsTabV2: React.FC = () => {
  const dispatch = useDispatch();
  const funds = useSelector(selectors.indexFundsList);
  const waiting = useSelector(selectors.waiting);
  const previewData = useSelector(selectors.indexFundPreviewData);
  const sessionsList = useSelector(selectors.sessions);

  const [query, setQuery] = useState('');
  const [assetClass, setAssetClass] = useState<string | undefined>();
  const [geography, setGeography] = useState<string | undefined>();
  const [eodFilter, setEodFilter] = useState<'on' | 'off' | undefined>();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('edit');
  const [selectedFund, setSelectedFund] = useState<IndexFund | null>(null);

  const [dataOpsOpen, setDataOpsOpen] = useState(false);
  const [dataOpsFund, setDataOpsFund] = useState<IndexFund | null>(null);

  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (previewData) setShowPreview(true);
  }, [previewData]);

  useEffect(() => {
    return () => {
      dispatch(clearIndexFundPreview({}));
    };
  }, [dispatch]);

  const sessionsById = useMemo(() => {
    const map: Record<number, SessionLite> = {};
    (sessionsList || []).forEach((s: SessionLite) => {
      map[s.id] = s;
    });
    return map;
  }, [sessionsList]);

  const activeFunds = useMemo(
    () => funds.filter(f => !f.decommissioned),
    [funds],
  );
  const decommissionedFunds = useMemo(
    () => funds.filter(f => f.decommissioned),
    [funds],
  );

  const assetClassOptions = useMemo(
    () =>
      Array.from(
        new Set(
          activeFunds.map(f => f.asset_class).filter(Boolean) as string[],
        ),
      ).sort(),
    [activeFunds],
  );
  const geographyOptions = useMemo(
    () =>
      Array.from(
        new Set(activeFunds.map(f => f.geography).filter(Boolean) as string[]),
      ).sort(),
    [activeFunds],
  );

  const visibleFunds = useMemo(() => {
    const q = query.trim().toLowerCase();
    return activeFunds.filter(f => {
      if (assetClass && f.asset_class !== assetClass) return false;
      if (geography && f.geography !== geography) return false;
      if (eodFilter === 'on' && !f.fetch_eod) return false;
      if (eodFilter === 'off' && f.fetch_eod) return false;
      if (!q) return true;
      return (
        f.ric.toLowerCase().includes(q) ||
        f.name.toLowerCase().includes(q) ||
        (f.issuer_name || '').toLowerCase().includes(q)
      );
    });
  }, [activeFunds, query, assetClass, geography, eodFilter]);

  const openCreate = () => {
    setSelectedFund(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  };
  const openEdit = (fund: IndexFund) => {
    setSelectedFund(fund);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };
  const toggleEod = (fund: IndexFund, value: boolean) =>
    dispatch(updateIndexFund({ ...fund, fetch_eod: value }));
  const showEod = (fund: IndexFund) =>
    dispatch(requestIndexFundPreview({ index_fund_id: fund.id }));
  const decommission = (fund: IndexFund) =>
    dispatch(updateIndexFund({ ...fund, decommissioned: true }));
  const openDataOps = (fund: IndexFund | null) => {
    setDataOpsFund(fund);
    setDataOpsOpen(true);
  };

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Create
        </Button>
        <Button icon={<ImportOutlined />} onClick={() => openDataOps(null)}>
          Import / refresh data
        </Button>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search RIC, name, issuer"
          style={{ width: 240 }}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <Select
          allowClear
          placeholder="Asset class"
          style={{ width: 150 }}
          value={assetClass}
          onChange={setAssetClass}
          options={assetClassOptions.map(v => ({ label: v, value: v }))}
        />
        <Select
          allowClear
          placeholder="Geography"
          style={{ width: 150 }}
          value={geography}
          onChange={setGeography}
          options={geographyOptions.map(v => ({ label: v, value: v }))}
        />
        <Select
          allowClear
          placeholder="Fetch EoD"
          style={{ width: 130 }}
          value={eodFilter}
          onChange={setEodFilter}
          options={[
            { label: 'EoD on', value: 'on' },
            { label: 'EoD off', value: 'off' },
          ]}
        />
      </Space>

      <FundsTable
        funds={visibleFunds}
        loading={waiting}
        sessionsById={sessionsById}
        onEdit={openEdit}
        onToggleEod={toggleEod}
        onShowEod={showEod}
        onDecommission={decommission}
      />

      <Collapse style={{ marginTop: 16 }}>
        <Collapse.Panel
          header={`Decommissioned funds (${decommissionedFunds.length})`}
          key="decommissioned"
        >
          {decommissionedFunds.length === 0 ? (
            <Text type="secondary">No decommissioned funds.</Text>
          ) : (
            <EditableTable
              data={decommissionedFunds}
              onSave={(a: IndexFund) => dispatch(updateIndexFund(a))}
              onDelete={(item: IndexFund) => dispatch(deleteIndexFund(item))}
              ignoredFields={['decommissioned', 'hidden']}
              nonEditableFields={['in_playing_sessions', 'in_paused_sessions']}
              buttons={[
                {
                  onClick: (record: IndexFund) =>
                    dispatch(
                      requestIndexFundPreview({ index_fund_id: record.id }),
                    ),
                  title: 'Show EoD',
                },
                {
                  onClick: (record: IndexFund) =>
                    dispatch(
                      updateIndexFund({ ...record, decommissioned: false }),
                    ),
                  title: 'Restore',
                },
              ]}
            />
          )}
        </Collapse.Panel>
      </Collapse>

      <FundDrawer
        open={drawerOpen}
        mode={drawerMode}
        fund={selectedFund}
        sessionsById={sessionsById}
        onClose={() => setDrawerOpen(false)}
        onShowEod={showEod}
        onRefreshData={openDataOps}
      />

      <DataOpsModal
        open={dataOpsOpen}
        fund={dataOpsFund}
        onClose={() => setDataOpsOpen(false)}
      />

      {showPreview && previewData && (
        <IndexFundPreviewModal
          visible={showPreview}
          hide={() => {
            setShowPreview(false);
            dispatch(clearIndexFundPreview({}));
          }}
        />
      )}
    </>
  );
};

export default IndexFundsTabV2;
